import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  applyNextRevision,
  applyPreviousRevision,
  isNextRevisionExists,
  isPrevRevisionExists
} from 'modules/revisions';
import Icon from 'shared/Icon';
import { ISvgIconProps } from '@picsart/rc/types/components/SvgIcon/types';
import Tooltip, { placementsList } from '@picsart/rc/old/Tooltip';
import { useTranslator } from '@picsart/rc/services/localization';

const Btn = (props: {
  handler: () => void;
  icon: ISvgIconProps['name'];
  tooltip: string;
  disabled: boolean;
  'data-test': string;
}) => {
  const { handler, icon, disabled, tooltip } = props;
  return (
    <Tooltip placement={placementsList.top} content={tooltip}>
      <span>
        <button
          sx={{
            all: 'unset',
            display: 'block',
            py: '14px',
            cursor: 'pointer',
            paddingLeft: icon === 'undo-editor' ? '12px' : '6px',
            paddingRight: icon === 'undo-editor' ? '6px' : '12px',
            svg: {
              fill: 'grey3'
            },
            '&:disabled': {
              cursor: 'default',
              pointerEvents: 'none',
              '& svg path': {
                fill: 'grey10'
              }
            }
          }}
          data-test={props['data-test']}
          onClick={handler}
          disabled={disabled}
        >
          <Icon name={icon} sx={{ width: 24, height: 24 }} />
        </button>
      </span>
    </Tooltip>
  );
};

export const UndoRedoPanel: FC = () => {
  const dispatch = useDispatch();
  const translator = useTranslator();

  const isRedoAvailable = useSelector(isNextRevisionExists);
  const isUndoAvailable = useSelector(isPrevRevisionExists);

  const undoHandler = () => dispatch(applyPreviousRevision());
  const redoHandler = () => dispatch(applyNextRevision());

  return (
    <div
      sx={{
        position: 'absolute',
        bottom: 0,
        left: '50%',
        display: 'flex',
        justifyContent: 'center',
        transform: 'translateX(-50%)',
        py: '20px'
      }}
    >
      <div
        sx={{
          height: '52px',
          bg: 'white',
          display: 'flex',
          borderRadius: '8px'
        }}
      >
        <Btn
          tooltip={translator('Undo')}
          disabled={!isUndoAvailable}
          handler={undoHandler}
          icon="undo-editor"
          data-test="revisions-button-undo"
        />
        <Btn
          tooltip={translator('Redo')}
          disabled={!isRedoAvailable}
          handler={redoHandler}
          icon="redo-editor"
          data-test="revisions-button-redo"
        />
      </div>
    </div>
  );
};
